"use client";
import React, { useState, useRef, useEffect } from "react";
import Image from "next/image";
import defaultProfile from "@/images/default-profile.jpg";
import {
  Image as ImageIcon,
  Link as LinkIcon,
  MapPin,
  X,
  Smile,
  Hash,
  AtSign,
  AlertTriangle,
  HandHeart,
  ChevronDown,
} from "lucide-react";
import TextareaAutosize from "react-textarea-autosize";
import LinkPreview from "../LinkPreview";
import { usePostContext } from "@/contexts/PostContext";
import { useMutation, useReactiveVar } from "@apollo/client";
import { ADD_POST, ADD_TAG } from "@/api/apollo-client/mutations/mutations";
import { useAuth } from "@/contexts/authContext";
import SuggestionSearch from "../SuggestionSearch";
import data from "@emoji-mart/data";
import Picker from "@emoji-mart/react";
import toast from "react-hot-toast";
// import { useTheme } from '@/components/theme-provider';
import { useRouter } from "next/navigation";
import { useTheme } from "next-themes";
import type { Mentions } from "../AssociationWith";

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { PostType } from "@/constants";
import Head from "next/head";
import { useTextInput } from "@/hooks/Pagination/useTextInput";
import { useEmojiPicker } from "@/hooks/useEmojiPicker";
import { useMediaUpload } from "@/hooks/useMediaUpload";
import AssociationWith from "../AssociationWith";
import PostDisplay from "./PostDisplay";
import { sendClickEvent } from "@/components/Analytics";

interface PostCardProps {
  onPostSubmit?: (postData: any) => void;
  scrollRef?: React.RefObject<HTMLDivElement>;
}

interface PostVariables {
  author_id: string;
  type: PostType;
  content: string;
  tags: string[];
  mentioned: string[];
  associated_with: string[];
  likes: number;
  comments: number;
  links: string[];
  location?: {
    type: string;
    coordinates: number[];
  };
  location_name?: string | null;
  media?: string[];
}
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

import { Button } from "@/components/ui/button";
import LocationMapPicker from "../LocationMapPicker";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";
import Modal from "../Modal/Modal";
import GmapModal from "../gmapModal";
import VideoPlayer from "./VideoPlayer";
import { buttonTooltipDismissedVar } from "../HomePage";
import { isNull, set } from "lodash";
import { Item } from "@radix-ui/react-dropdown-menu";

const PostCard: React.FC<PostCardProps> = ({ scrollRef }) => {
  const { user } = useAuth();
  const [postType, setPostType] = useState<PostType>(PostType.GENERAL);
  const [floatCordinate, setfloatCoordinate] = useState<number[] | undefined>(
    undefined
  );
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [locationName, setLocationName] = useState<string | null>(null);
  const [submitted, setsubmitted] = useState<boolean>(false);
  const [showUploadPopup, setShowUploadPopup] = useState<boolean>(false);
  const [isUploadPaused, setIsUploadPaused] = useState<boolean>(false);
  const pauseRef = useRef(false);
  const cancelUploadRef = useRef(false);
  const [hasTriedInteraction, setHasTriedInteraction] =
    useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const { theme, setTheme } = useTheme();

  const [selected, setSelected] = useState<Mentions[]>([]);
  const [modalOpen, setModalOpen] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);

  selected;
  const [isDragging, setIsDragging] = useState(false);
  const dragCounter = useRef(0);

  // Use our custom hooks
  const {
    text,
    setText,
    backendText,
    setBackendText,
    charCount,
    links,
    tags,
    mentions,
    selectedTags,
    setSelectedTags,
    selectedMentions,
    setSelectedMentions,
    rebuildBackendText,
  } = useTextInput();

  const { showEmojiPicker, setShowEmojiPicker, pickerRef, handleEmojiSelect } =
    useEmojiPicker();

  const {
    media,
    mediaPreview,
    fileInputRef,
    handleMediaUpload,
    removeMedia,
    uploadMedia,
    resetMedia,
    handleDroppedFiles,
    isVideoFile,
  } = useMediaUpload({ userId: user?.id });

  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const [addPostMutation] = useMutation(ADD_POST);
  const [addTagMutation] = useMutation(ADD_TAG);
  const [isTextType, setIsTextType] = useState(false);

  const [locationCoordinates, setLocationCoordinates] = useState<{
    type: string;
    coordinates: number[];
  } | null>(null);

  const insertTextAtCursor = (textToInsert: string) => {
    if (!textAreaRef.current) return;

    const cursorPosition = textAreaRef.current.selectionStart;
    const textBefore = text.substring(0, cursorPosition);
    const textAfter = text.substring(cursorPosition);

    setText(textBefore + textToInsert + textAfter);

    // Move cursor to end of inserted text
    setTimeout(() => {
      if (textAreaRef.current) {
        textAreaRef.current.focus();
        const newPosition = cursorPosition + textToInsert.length;
        textAreaRef.current.setSelectionRange(newPosition, newPosition);
      }
    }, 0);
  };

  //Handle Post Submission
  const handleSubmit = async () => {
    if (text.trim() === "" && media.length === 0) return;

    setShowUploadPopup(false);
    setHasTriedInteraction(false);
    setIsUploading(true);
    setIsUploadPaused(false);
    pauseRef.current = false;
    cancelUploadRef.current = false;

    setUploadProgress(5); // Starting

    let createdPostData: any;
    try {
      const tagRegex = /#([\p{L}\p{M}\p{N}_]+)/gu;
      const foundTags = [];
      let match;
      while ((match = tagRegex.exec(text)) !== null) foundTags.push(match[1]);

      const collator = new Intl.Collator("en", { sensitivity: "base" });
      const missingTags = foundTags.filter(
        (tag) => !selectedTags.some((t) => collator.compare(t.value, tag) === 0)
      );

      let newTags = [];
      for (const tag of missingTags) {
        const { data } = await addTagMutation({ variables: { tag_name: tag } });
        if (data?.addTag) newTags.push({ id: data.addTag, value: tag });
      }

      let allTags = selectedTags;
      if (newTags.length > 0) {
        allTags = [...selectedTags, ...newTags];
        setSelectedTags(allTags);
      }

      setBackendText(rebuildBackendText(text, allTags, selectedMentions));

      setUploadProgress(15); // Tags processed

      await waitWhilePaused(); // Optional pause hook

      // Upload with real-time tracking (15–85%)
      const uploadedMedia = await uploadMedia((percent) => {
        const scaled = Math.floor(15 + percent * 0.7); // e.g. scale 15–85%
        setUploadProgress(scaled);
      });

      const mediaIds = uploadedMedia.map((m) => m.id);
      const formattedBackendText = rebuildBackendText(
        text,
        allTags,
        selectedMentions
      );

      const escapeRegExp = (string: string) => {
        return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      };

      const tagsInContent = allTags.filter((tag) =>
        new RegExp(
          `#${escapeRegExp(tag.value)}(?![\\p{L}\\p{M}\\p{N}_])`,
          "iu"
        ).test(text)
      );

      let variables: PostVariables = {
        author_id: user?.id || "",
        type: postType,
        content: formattedBackendText,
        tags: tagsInContent.map((tag) => tag.id),
        mentioned: selectedMentions.map((m) => m.id),
        associated_with: selected.map((m) => m.id),
        likes: 0,
        comments: 0,
        links: [...links],
      };

      if (locationCoordinates) {
        variables.location = locationCoordinates;
        variables.location_name = locationName;
      }

      if (mediaIds.length > 0) {
        variables.media = mediaIds;
      }
      if (postType === PostType.GENERAL) {
        variables.associated_with = [];
      }

      await waitWhilePaused();

      setUploadProgress(90); // Before sending final mutation

      const { data, errors } = await addPostMutation({ variables });
      createdPostData = data;

      if (errors) {
        console.error("GraphQL Errors:", errors);
        toast.error("Failed to create post");
        return;
      }

      setsubmitted(true);
      setText("");
      setBackendText("");
      setPostType(PostType.GENERAL);
      setfloatCoordinate(undefined);
      setLocationName(null);
      setLocationCoordinates(null);
      resetMedia();
      isVideoFile
        ? toast.success(
            "Your post has been created! It will visible in your feed in few minutes"
          )
        : toast.success("Your post has been created!");
      setTimeout(() => setUploadProgress(0), 500);
    } catch (error) {
      console.error("Error creating post:", (error as Error).message);
      toast.error("Failed to create post");
    } finally {
      setIsUploading(false);
      setIsUploadPaused(false);
      pauseRef.current = false;
    }
    sendClickEvent({
      category: "Button",
      action: "post_create",
      label: "create a post",
      value: 1,
      extraParams: {
        postId: createdPostData?.addPost?.id,
      },
    });
  };

  const waitWhilePaused = async () => {
    while (pauseRef.current) {
      if (cancelUploadRef.current) {
        throw new Error("UploadCancelled");
      }
      await new Promise((resolve) => setTimeout(resolve, 300));
    }
    if (cancelUploadRef.current) {
      throw new Error("UploadCancelled");
    }
  };

  useEffect(() => {
    const handleBackNavigation = (event: PopStateEvent) => {
      if (isUploading && !cancelUploadRef.current && !pauseRef.current) {
        event.preventDefault();
        setIsUploadPaused(true);
        setShowUploadPopup(true);
        setHasTriedInteraction(true);
        window.history.pushState(null, "", window.location.href); // cancel navigation
      }
    };

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (isUploading && !cancelUploadRef.current && !pauseRef.current) {
        setIsUploadPaused(true);
        setShowUploadPopup(true);
        setHasTriedInteraction(true);

        // prevent upload in case user proceeds
        cancelUploadRef.current = true;

        event.preventDefault();
        event.returnValue = "";
        return "";
      }
    };

    if (isUploading) {
      window.history.pushState(null, "", window.location.href);
      window.addEventListener("popstate", handleBackNavigation);
      window.addEventListener("beforeunload", handleBeforeUnload);
    }

    return () => {
      window.removeEventListener("popstate", handleBackNavigation);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isUploading]);

  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const items = e.clipboardData.items;
    const files: File[] = [];

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.kind === "file") {
        const file = item.getAsFile();
        if (
          file &&
          (file.type.startsWith("image/") || file.type.startsWith("video/"))
        ) {
          files.push(file);
        }
      }
    }

    if (files.length > 0) {
      if (mediaPreview.length + files.length > 4) {
        toast.error("You can add only 4 items");

        e.preventDefault();
        return;
      }
      handleDroppedFiles(files); // type-safe cast
    }
  };

  useEffect(() => {
    if (!isUploading) {
      setShowUploadPopup(false);
      setHasTriedInteraction(false);
      setIsUploadPaused(false);
    }
  }, [isUploading]);

  useEffect(() => {
    pauseRef.current = isUploadPaused;
  }, [isUploadPaused]);

  useEffect(() => {
    const handleBackNavigation = (event: PopStateEvent) => {
      if (isUploading && !cancelUploadRef.current && !pauseRef.current) {
        event.preventDefault();
        setIsUploadPaused(true);
        setShowUploadPopup(true);
        setHasTriedInteraction(true);
        window.history.pushState(null, "", window.location.href); // cancel navigation
      }
    };

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (isUploading && !cancelUploadRef.current && !pauseRef.current) {
        setIsUploadPaused(true);
        setShowUploadPopup(true);
        setHasTriedInteraction(true);

        // prevent upload in case user proceeds
        cancelUploadRef.current = true;

        event.preventDefault();
        event.returnValue = "";
        return "";
      }
    };

    if (isUploading) {
      window.history.pushState(null, "", window.location.href);
      window.addEventListener("popstate", handleBackNavigation);
      window.addEventListener("beforeunload", handleBeforeUnload);
    }

    return () => {
      window.removeEventListener("popstate", handleBackNavigation);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isUploading]);

  // Remove a single association by id
  const handleRemoveAssociation = (id: string) => {
    setSelected((prev) => prev.filter((item) => item.id !== id));
  };

  const dismissed = useReactiveVar(buttonTooltipDismissedVar);

  const dismissTooltip = () => {
    buttonTooltipDismissedVar(true);
    localStorage.setItem("buttonTooltipDismissed", "true");
  };

  return (
    <div
      ref={scrollRef}
      className="md:p-4 p-2 border-b border-border bg-card text-foreground shadow-xl rounded-lg backdrop-blur-lg border-t-[0.5px] mb-3 md:mb-4 mt-3 layout-theme"
    >
      <div className="flex  flex-wrap gap-1 md:p-2 lg:p-2">
        <div className=" mr-2">
          <Image
            src={user?.profile_picture?.url ?? defaultProfile}
            alt="User avatar"
            width={60}
            height={60}
            className="w-10 h-10 sm:w-12 sm:h-12 rounded-full"
          />
        </div>
        <div className="flex-grow mr-3 ">
          {/* Post type selector */}
          <div className="flex mb-2 overflow-x-auto hide-scrollbar">
            <div className="flex space-x-1 sm:space-x-2 text-xs sm:text-sm">
              <button
                className={`inline-flex max-w-full items-center gap-2 px-3 py-1 border rounded-full transition
                  ${
                    postType === PostType.PROBLEM
                      ? theme === "dark"
                        ? "bg-gradient-to-r from-red-500 via-red-600 to-red-700 text-gray-100 font-normal shadow-md border-red-700"
                        : "bg-gradient-to-r from-red-500 via-red-600 to-red-700 text-gray-800 font-normal shadow-md border-red-600"
                      : theme === "dark"
                        ? "border-gray-700 text-gray-200 shadow hover:shadow-md md:hover:bg-gradient-to-r md:hover:from-red-800 md:hover:to-pink-800 md:hover:text-white"
                        : "border-gray-300 text-gray-800 shadow hover:shadow-md md:hover:bg-gradient-to-r md:hover:from-red-700 md:hover:to-pink-700 md:hover:text-white"
                  }
                `}
                onClick={() =>
                  setPostType(
                    postType === PostType.PROBLEM
                      ? PostType.GENERAL
                      : PostType.PROBLEM
                  )
                }
              >
                <AlertTriangle className="w-4 h-4 mr-[-1px] z-50" />
                Problem
              </button>
              <button
                className={`inline-flex max-w-full items-center gap-2 px-3 py-1 border rounded-full transition
                  ${
                    postType === PostType.PRAISE
                      ? theme === "dark"
                        ? "bg-gradient-to-tr from-yellow-500 to-yellow-200 text-black font-normal shadow-md border-yellow-600"
                        : "bg-gradient-to-tr from-[#e0aa3e] to-[#f9f295] text-black font-normal shadow-md border-yellow-400"
                      : theme === "dark"
                        ? "border-gray-700 text-gray-200 shadow hover:shadow-md md:hover:bg-gradient-to-r md:hover:from-yellow-800 md:hover:to-yellow-400 md:hover:text-black"
                        : "border-gray-300 text-gray-800 shadow hover:shadow-md md:hover:bg-gradient-to-r md:hover:from-[#e0aa3e] md:hover:to-[#f9f295] md:hover:text-primary"
                  }
                `}
                onClick={() =>
                  setPostType(
                    postType === PostType.PRAISE
                      ? PostType.GENERAL
                      : PostType.PRAISE
                  )
                }
              >
                <HandHeart className="w-4 h-4" /> Praise
              </button>
            </div>
          </div>
          <div className="flex mb-2 overflow-x-auto hide-scrollbar">
            <div className="flex space-x-1 sm:space-x-2 text-xs sm:text-sm">
              {postType !== PostType.GENERAL && (
                <div className="flex items-center sm:h-6">
                  <AssociationWith
                    onSelect={setSelected}
                    selected={selected}
                    postType={postType}
                  />
                </div>
              )}
              {postType !== PostType.GENERAL && selected.length > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      className="h-6 py-2 w-30 border rounded focus:outline-none relative inline-flex items-center gap-1 px-2 py-1 border rounded-full transition"
                    >
                      <div className="truncate w-10">{selected[0]?.name}</div>
                      <ChevronDown />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    className="w-56"
                    align="start"
                    side="bottom"
                    sideOffset={4}
                  >
                    <DropdownMenuLabel>Associated with</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {selected.slice(0).map((item, index) => (
                      <DropdownMenuItem key={index}>
                        <div className="flex items-center gap-2 w-full">
                          <Image
                            src={item.profile_picture?.url || defaultProfile}
                            alt={item.name}
                            width={28}
                            height={28}
                            className="w-7 h-7 rounded-full object-cover"
                            loading="lazy"
                          />
                          <div>{item.name}</div>
                          <button
                            className="w-4 h-4 flex items-center justify-center ml-auto"
                            onClick={() => {
                              handleRemoveAssociation(item.id);
                            }}
                          >
                            <X />
                          </button>
                        </div>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>
          <div
            onDragOver={(e) => {
              e.preventDefault();
            }}
            onDragEnter={(e) => {
              e.preventDefault();
              dragCounter.current += 1;
              setIsDragging(true);
            }}
            onDragLeave={(e) => {
              dragCounter.current -= 1;
              if (dragCounter.current === 0) {
                setIsDragging(false);
              }
            }}
            onDrop={(e) => {
              e.preventDefault();
              dragCounter.current = 0;
              setIsDragging(false);
              handleDroppedFiles(Array.from(e.dataTransfer.files));
            }}
            className={`w-full p-2 rounded-md border-2 border-dashed transition-colors duration-150 ${
              isDragging ? "border-blue-500 bg-blue-50" : "border-transparent"
            }`}
          >
            {/* Text input */}
            <TextareaAutosize
              ref={textAreaRef}
              value={text}
              onChange={(e) => setText(e.target.value)}
              onPaste={handlePaste}
              placeholder="What's happening?"
              minRows={2}
              maxRows={12}
              className="w-full bg-transparent text-base sm:text-lg outline-none resize-none placeholder:text-muted-foreground"
              maxLength={2000}
              onClick={() => setIsTextType(true)}
            />

            {/* Hashtag Suggestions */}
            <SuggestionSearch
              text={text}
              setText={setText}
              backendText={backendText}
              setBackendText={setBackendText}
              onSelectMention={(mention) => {
                setSelectedMentions((prev) => {
                  const newMentions = prev.some((m) => m.id === mention.id)
                    ? prev
                    : [...prev, mention];
                  return newMentions;
                });
              }}
              onSelectTag={(tag) => {
                setSelectedTags((prev) => {
                  const newTags = prev.some((t) => t.id === tag.id)
                    ? prev
                    : [...prev, tag];
                  return newTags;
                });
              }}
              textareaRef={textAreaRef}
            />

            {/* Character count */}
            <div
              className={`text-xs text-right mr-2 ${charCount > 2000 * 0.9 ? "text-destructive" : "text-muted-foreground"}`}
            >
              {charCount}/2000
            </div>

            {/* Link previews */}
            {links.length > 0 && (
              <div className="mt-2 space-y-2 max-w-full overflow-hidden">
                {links.map((link, index) => (
                  <LinkPreview key={index} url={link} />
                ))}
              </div>
            )}

            {/* Media previews */}
            {mediaPreview.length > 0 && (
              <div className="mt-2">
                <Carousel>
                  <CarouselContent>
                    {mediaPreview.map((src, index) => (
                      <CarouselItem key={index}>
                        <div className="relative rounded-md overflow-hidden w-full h-48 sm:h-64">
                          {media[index].type.startsWith("video") ? (
                            <VideoPlayer videoUrl={src} />
                          ) : (
                            <Image
                              src={src}
                              alt={`Preview ${index}`}
                              style={{ objectFit: "cover" }}
                              fill={true} // Ensures image covers the container
                            />
                          )}
                          <button
                            className="absolute top-2 right-2 bg-black/60 rounded-full p-1 hover:bg-black/80 transition-colors"
                            onClick={() => removeMedia(index)}
                          >
                            <X size={16} className="text-white" />
                          </button>
                        </div>
                      </CarouselItem>
                    ))}
                  </CarouselContent>

                  {mediaPreview.length > 1 && (
                    <>
                      <CarouselPrevious className="absolute left-2 top-1/2 -translate-y-1/2 z-20 bg-black/50 p-2 rounded-full text-white disabled:hidden" />
                      <CarouselNext className="absolute right-2 top-1/2 -translate-y-1/2 z-20 bg-black/50 p-2 rounded-full text-white disabled:hidden" />
                    </>
                  )}
                </Carousel>
              </div>
            )}

            {/* Location display */}
            {locationCoordinates && (
              <div className="mt-2 flex items-center text-xs sm:text-sm text-muted-foreground relative group">
                <MapPin size={14} className="mr-1 flex-shrink-0" />
                <span className="truncate w-60 ">
                  {locationName
                    ? locationName
                    : `${locationCoordinates.coordinates[0]}, ${locationCoordinates.coordinates[1]}`}
                </span>
                <span className="absolute z-50 left-4 top-0 mt-4 hidden group-hover:block bg-green-600 text-white text-sm px-2 py-1 rounded text-wrap">
                  {locationName
                    ? locationName
                    : `${locationCoordinates.coordinates[0]}, ${locationCoordinates.coordinates[1]}`}
                </span>
                <button
                  className="ml-1 text-destructive hover:text-destructive/80 flex-shrink-0"
                  onClick={() => {
                    setLocationCoordinates(null);
                    setLocationName(null);
                  }}
                >
                  <X size={19} className="" />
                </button>
              </div>
            )}

            {/* Action buttons */}
            <div className="flex flex-wrap justify-between items-center mt-2 sm:mt-3">
              <div className="flex flex-wrap -ml-1">
                {/* Media upload button */}
                <button
                  className="text-primary hover:text-primary-foreground p-2 rounded-full hover:bg-primary/10 transition-colors focus:outline-none focus:ring-2 focus:ring-ring/40"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={
                    media.length >= 4 ||
                    media.some((file) => file.type.startsWith("video/"))
                  }
                  title={
                    media.length >= 4
                      ? "Maximum media limit reached"
                      : "Add photos or video"
                  }
                >
                  <ImageIcon size={20} />
                </button>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleMediaUpload}
                  className="hidden"
                  accept="image/*,video/*"
                  multiple
                />

                {/* Location button */}
                <div className="relative inline-block">
                  <button
                    id="tooltipButton"
                    type="button"
                    className="text-primary hover:text-primary-foreground p-2 rounded-full hover:bg-primary/10 transition-colors focus:outline-none focus:ring-2 focus:ring-ring/40"
                    onClick={async () => {
                      if (!navigator.geolocation) {
                        toast.error(
                          "Geolocation is not supported by your browser"
                        );
                        return;
                      }
                      navigator.geolocation.getCurrentPosition(
                        (position) => {
                          const lat = position.coords.latitude;
                          const lng = position.coords.longitude;
                          const locationObj = {
                            type: "Point",
                            coordinates: [lng, lat],
                          };
                          setLocationCoordinates(locationObj);

                          // Reverse geocode
                          if (
                            window.google &&
                            window.google.maps &&
                            window.google.maps.Geocoder
                          ) {
                            const geocoder = new window.google.maps.Geocoder();
                            geocoder.geocode(
                              { location: { lat, lng } },
                              (results, status) => {
                                if (status === "OK" && results && results[0]) {
                                  const components =
                                    results[0].address_components;
                                  let city = "";
                                  let state = "";
                                  let country = "";

                                  components.forEach((component) => {
                                    if (
                                      component.types.includes(
                                        "administrative_area_level_2"
                                      )
                                    ) {
                                      city = component.long_name;
                                    } else if (
                                      component.types.includes(
                                        "administrative_area_level_1"
                                      )
                                    ) {
                                      state = component.long_name;
                                    } else if (
                                      component.types.includes("country")
                                    ) {
                                      country = component.long_name;
                                    }
                                  });

                                  const formatted = [city, state, country]
                                    .filter(Boolean)
                                    .join(", ");
                                  setLocationName(
                                    formatted || results[0].formatted_address
                                  );
                                }
                              }
                            );
                          }
                        },
                        () => {
                          toast.error("Unable to retrieve your location");
                        }
                      );
                      setIsModalOpen(true);
                    }}
                  >
                    <MapPin size={20} />
                  </button>
                </div>

                <GmapModal
                  isOpen={isModalOpen}
                  onClose={() => setIsModalOpen(false)}
                  title="Pick a location for your post"
                  onSelectLocation={({
                    lat,
                    lng,
                    address,
                  }: {
                    lat: number;
                    lng: number;
                    address?: string;
                  }) => {
                    setLocationCoordinates({
                      type: "Point",
                      coordinates: [lng, lat],
                    });
                    setLocationName(address || "");
                  }}
                />

                {/* Emoji button */}
                <div className="relative" ref={pickerRef}>
                  <button
                    className="text-primary  hover:text-primary-foreground p-2 rounded-full hover:bg-primary/10 transition-colors focus:outline-none focus:ring-2 focus:ring-ring/40 emoji-toggle-button"
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                  >
                    <Smile size={20} />
                  </button>

                  {showEmojiPicker && (
                    <div
                      className="absolute  mt-1 bg-background border border-border rounded-md shadow-lg justify-center content-center"
                      style={{
                        width: "min(95vw, 320px)",
                        height: "min(435px, 70vh)",
                        left: "100%",
                        transform: "translateX(-50%)",
                      }}
                    >
                      <Picker
                        data={data}
                        onEmojiSelect={(emoji: any) =>
                          handleEmojiSelect(emoji.native, insertTextAtCursor)
                        }
                        theme={theme}
                        previewPosition="none"
                        emojiSize={20}
                        maxFrequentRows={3}
                        style={{
                          "--category-tabs-height": "48px",
                          "--search-height": "40px",
                          width: "100%",
                          height: "100%",
                        }}
                      />
                    </div>
                  )}
                </div>

                {/* Hashtag and Mention buttons */}
                <button
                  className="text-primary hover:text-primary-foreground p-2 rounded-full hover:bg-primary/10 transition-colors focus:outline-none focus:ring-2 focus:ring-ring/40"
                  onClick={() => insertTextAtCursor("#")}
                >
                  <Hash size={20} />
                </button>
                <button
                  className="text-primary hover:text-primary-foreground p-2 rounded-full hover:bg-primary/10 transition-colors focus:outline-none focus:ring-2 focus:ring-ring/40"
                  onClick={() => insertTextAtCursor("@")}
                >
                  <AtSign size={20} />
                </button>
              </div>

              {/* Post button */}
              <button
                onClick={handleSubmit}
                disabled={
                  isUploading ||
                  (text.trim() === "" && media.length === 0) ||
                  charCount > 2000
                }
                className={`mt-2 sm:mt-0 px-5 py-2 rounded-full font-bold text-sm shadow-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-ring/40 ${
                  isUploading ||
                  (text.trim() === "" && media.length === 0) ||
                  charCount > 2000
                    ? "bg-black text-white cursor-not-allowed"
                    : "bg-black text-primary-foreground hover:bg-gray-800"
                }`}
              >
                {/* Progress background */}
                {isUploading && (
                  <div
                    className="absolute top-0 left-0 h-full bg-blue-500 opacity-40 z-0"
                    style={{
                      width: `${uploadProgress}%`,
                      transition: "width 0.3s",
                    }}
                  ></div>
                )}

                {/* Button text */}
                <span className="relative z-10 text-primary text-white">
                  {isUploading ? "Uploading" : "Post"}
                </span>
              </button>

              {isUploading && (
                <div className="fixed inset-0 z-40 pointer-events-none" />
              )}

              {showUploadPopup && (
                <div className="fixed inset-0 z-50 bg-black bg-opacity-40 flex items-center justify-center">
                  <div className="bg-white p-6 rounded-lg shadow-lg max-w-sm sm:w-full w-3/4 text-center">
                    <p className="text-gray-800 mb-4">
                      Upload is in progress. Do you want to quit the upload?
                    </p>
                    <div className="flex justify-center gap-4">
                      <button
                        className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600 mr-2"
                        onClick={() => {
                          setShowUploadPopup(false);
                          setIsUploadPaused(false);
                          cancelUploadRef.current = true;
                        }}
                      >
                        Yes
                      </button>
                      <button
                        className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                        onClick={() => {
                          setShowUploadPopup(false);
                          setIsUploadPaused(false);
                        }}
                      >
                        No
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PostCard;
